import React, { useEffect, useState } from 'react';
import { Formik, Form, FormikHelpers } from 'formik';
import { Tab, Container, Form as SForm, Header, Icon, Divider, Button, TabPane } from 'semantic-ui-react';
import { keyOfCheckerNested } from '@common/Utils';
import * as Lookups from '../Hooks/LookupOptions';
import { BPBadRequestResult, PersonClient, PersonDto } from '@app/api2/api';
import { PersonValidationSchema } from './EnrollmentForm.Schema';
import { buildUrl, AppRoutes } from '../../AppRoutes';
import { BiographicalTab } from './Tabs/BiographicalTab';
import { PhysicalTab } from './Tabs/PhysicalTab';
import { AdditionalTab } from './Tabs/AdditionalTab';
import { ProfessionalTab } from './Tabs/ProfessionalTab';
import { IdentificationTab } from './Tabs/IdentificationTab';
import { ContactTab } from './Tabs/ContactTab';
import SemanticFormikSelect from '@common/Forms/SemanticFormikSelect';
import { useUserLabs } from '../../Hooks/UseUserLabs';
import toast from 'react-hot-toast';
import { ALL_BIO_PROPERTIES } from './Tabs/BiographicalTab.Schema';
import { ALL_ADDITIONAL_PROPERTIES } from './Tabs/AdditionalTab.Schema';
import { ALL_CONTACT_PROPERTIES } from './Tabs/ContactTab.Schema';
import { ALL_EMPLOYMENT_PROPERTIES } from './Tabs/ProfessionalTab.Schema';
import { ALL_IDENTIFICATION_PROPERTIES } from './Tabs/IdentificationTab.Schema';
import { ALL_PHYSICAL_PROPERTIES } from './Tabs/PhysicalTab.Schema';
import { useMutation, useQuery } from '@tanstack/react-query';
import { personClient } from '@app/api2/Clients';
import { RoutedTab } from '../../CaseRun/Details/RoutedTab';
import { EnrollmentFormProvider } from './EnrollmentFormContext';
import { isArray } from 'lodash';
import { FormikWithSchema } from '@common/Forms/FormikWithSchema';
import { useLocation, useMatch, useMatches, useNavigate } from 'react-router';
import { useTranslation2 } from '../../Contexts/TranslationContext2';
import { BPButton } from '@/components/bp/bpbutton';
import { BPContainer } from '@/components/bp/bpcontainer';


const initialValues = {
    labId: null,
    // Tab 1
    enrollmentPurposeId: null,
    lastName: '',
    secondLastName: '',
    firstName: '',
    middleName: '',
    dob: '' as any,
    sex: '',
    sexId: null,
    nationality: '',
    nationalityId: null,
    agencyOri: '',
    agencyCaseNumber: '',
    fbiNumber: '',
    sidNumber: '',
    // Tab 2
    eyeColor: '',
    hairColor: '',
    heightUnit: 'm',
    heightMetric: '',
    heightFt: '',
    heightIn: '',
    weightUnit: 'kg',
    weight: '',
    pictures: [],
    physicalFeatures: [],
    // Tab 3
    addresses: [],
    phones: [],
    emails: [],
    // Tab 4
    andeSwabId: '',
    dnaKit: '',
    refProfileFile: null,
    identifications: [],
    // Tab 5
    employmentHistory: [],
    // Tab 6
    ethnicity: '',
    pobCity: '',
    pobCountry: '',
    aliases: [],
    fatherLastName: '',
    motherLastName: '',
    samples: [],
    ethnicityId: null,
    eyeColorId: null,
    hairColorId: null,
    isEnrollmentCompleted: false,

} as PersonDto;


const transformNulls = <T extends PersonDto>(result: T) => {
    // const transformNulls = <T extends Record<string, any>>(result: T) => {
    return {
        ...result,
        heightInM: result.heightInM ?? '' as any,
        weightInKg: result.weightInKg ?? '' as any,
        dob: result.dob ?? '' as any,
        // andeSwabId: result.andeSwabId ?? '',
        fbiNumber: result.fbiNumber ?? '',
        sidNumber: result.sidNumber ?? '',
        agencyCaseNumber: result.agencyCaseNumber ?? '',
        agencyOri: result.agencyOri ?? '',
        employmentHistory: [...(result.employmentHistory || []).map(emp => ({
            ...emp,
            startDate: emp.startDate ?? '' as any,
            endDate: emp.endDate ?? '' as any,
            employerAddressLine1: emp.employerAddressLine1 ?? '',
            employerAddressLine2: emp.employerAddressLine2 ?? '',
            employerPhone: emp.employerPhone ?? '',

        }))],
        addresses: [...(result.addresses || []).map(addr => ({
            ...addr,
            addressStartDate: addr.addressStartDate ?? '' as any,
            addressEndDate: addr.addressEndDate ?? '' as any,
            addressLine1: addr.addressLine1 ?? '',
            addressLine2: addr.addressLine2 ?? '',
            addressCityName: addr.addressCityName ?? '',
            addressStateName: addr.addressStateName ?? '',
            addressPostalCode: addr.addressPostalCode ?? '',
        }))],
        pictures: [...(result.pictures || [])],
        samples: [...(result.samples || [])].map(sample => ({
            ...sample,
            collectionDate: sample.collectionDate ?? '' as any,
            // collectedBy: sample.collectedBy ?? '',
            // fileContent: sample.fileContent ?? '',
            kitId: sample.kitId ?? null,
            sampleId: sample.sampleId ?? null,
            sampleName: sample.sampleName ?? '',
            collectorId: sample.collectorId ?? 0,
            collectorName: sample.collectorName ?? '',

        })),
        aliases: [...(result.aliases || [])],


    }
};



const EnrollmentForm = ({ metaId }) => {
    const match = useMatch(AppRoutes.ProfilesMetadataCreateAdditional);
    const match2 = useMatch(AppRoutes.ProfilesMetadataEditAdditional);
    const { translations: t } = useTranslation2();

    const isLastStep = match != null || match2 != null;
    // const history = useHistory();
    const location = useLocation();
    const navigate = useNavigate();

    const userLabs = useUserLabs({});

    const [initialState, setInitialState] = useState(metaId != null ? null : { ...initialValues });


    const personQuery = useQuery({
        queryKey: ['person', metaId],
        queryFn: async () => {
            if (!metaId) return null;
            const result = await personClient.getById(metaId);
            return transformNulls(result);
        },
        // onSuccess(data) {
        //     setInitialState(data ?? { ...initialValues, labId: userLabs.selectedLabId });
        // },
        gcTime: 0,
    });

    React.useEffect(() => {
        if (personQuery.data) {
            setInitialState(personQuery.data ?? { ...initialValues, labId: userLabs.selectedLabId });
        }
    }, [personQuery.data])

    const personMutator = useMutation({
        mutationFn: async ({ values }: { values: PersonDto, formikHelpers: FormikHelpers<PersonDto> }) => {
            let result = values.id == null ? await personClient.create(values) : await personClient.update(values);
            return result;
        },
        onSuccess: (data, variables) => {
            personQuery.refetch();
            toast.success('The enrollment was successfully saved');
            if (variables.values.id != data.id) {
                navigate(buildUrl(AppRoutes.ProfilesMetadataEdit, { metaId: data.id }));
            }
        },
        onError: (error, variables) => {
            // if ((error as BPBadRequestResult).modelErrors)
            //     variables.formikHelpers.setErrors((error as BPBadRequestResult).modelErrors)
        }
    });

    const panes = [
        {
            path: '/',
            to: metaId ? buildUrl(AppRoutes.ProfilesMetadataEditBio, { metaId: metaId ?? 0 }) : buildUrl(AppRoutes.ProfilesMetadataCreateBio, {}),
            menuItem: { key: 'bio', icon: 'user', content: 'Biographical' },
            fields: ALL_BIO_PROPERTIES,
            render: () => (<TabPane><BiographicalTab /></TabPane>),
        },
        {
            path: 'physical',
            to: metaId ? buildUrl(AppRoutes.ProfilesMetadataEditPhysical, { metaId: metaId ?? 0 }) : buildUrl(AppRoutes.ProfilesMetadataCreatePhysical, {}),
            menuItem: { key: 'phys', icon: 'eye', content: 'Physical' },
            fields: ALL_PHYSICAL_PROPERTIES,
            render: () => (<TabPane><PhysicalTab /></TabPane>),
        },
        {
            path: 'contact',
            to: metaId ? buildUrl(AppRoutes.ProfilesMetadataEditContact, { metaId: metaId ?? 0 }) : buildUrl(AppRoutes.ProfilesMetadataCreateContact, {}),
            menuItem: { key: 'contact', icon: 'phone', content: 'Contact' },
            fields: ALL_CONTACT_PROPERTIES,
            render: () => (<TabPane><ContactTab /></TabPane>),
        },
        {
            path: 'identification',
            to: metaId ? buildUrl(AppRoutes.ProfilesMetadataEditIdentification, { metaId: metaId ?? 0 }) : buildUrl(AppRoutes.ProfilesMetadataCreateIdentification, {}),
            menuItem: { key: 'id', icon: 'shield', content: 'Identification' },
            fields: ALL_IDENTIFICATION_PROPERTIES,
            render: () => (<TabPane><IdentificationTab /></TabPane>),
        },
        {
            path: 'employment',
            to: metaId ? buildUrl(AppRoutes.ProfilesMetadataEditEmployment, { metaId: metaId ?? 0 }) : buildUrl(AppRoutes.ProfilesMetadataCreateEmployment, {}),
            menuItem: { key: 'prof', icon: 'briefcase', content: 'Professional' },
            fields: ALL_EMPLOYMENT_PROPERTIES,
            render: () => (<TabPane><ProfessionalTab /></TabPane>),
        },
        {
            path: 'additional',
            to: metaId ? buildUrl(AppRoutes.ProfilesMetadataEditAdditional, { metaId: metaId ?? 0 }) : buildUrl(AppRoutes.ProfilesMetadataCreateAdditional, {}),
            menuItem: { key: 'add', icon: 'info', content: 'Additional' },
            fields: ALL_ADDITIONAL_PROPERTIES,
            render: () => (<TabPane><AdditionalTab /></TabPane>),
        },
    ];







    return (initialState &&
        <BPContainer>
            <EnrollmentFormProvider>
                <FormikWithSchema
                    validateOnBlur={true}
                    validateOnChange={false}
                    validateOnMount={true}
                    initialValues={initialState != null ? initialState : initialValues}
                    validationSchema={PersonValidationSchema(t)}
                    enableReinitialize={true}
                    onSubmit={async (values: PersonDto, formikHelpers: FormikHelpers<PersonDto>) => {
                        return await personMutator.mutateAsync({ values, formikHelpers });
                    }}>
                    {({ values, handleSubmit, errors, isValid, setFieldValue }) => (
                        <SForm as={Form} onSubmit={handleSubmit}>
                            {!userLabs.singleLab && <SemanticFormikSelect disabled={values.id != null} name="labId" label={window.translation.common.lab} {...userLabs?.getDropdownProps({})} />}

                            <Header as="h1" textAlign="center">
                                Forensic Enrollment System
                            </Header>
                            <RoutedTab
                                menu={{ pointing: true, secondary: true }}
                                tabs={panes.map((pane) => ({
                                    ...pane,
                                    exact: true,
                                    enabled: true,
                                    menuItem: {
                                        ...pane.menuItem,
                                        content: <>
                                            {pane.menuItem.content}
                                            {errors && pane.fields.findIndex((pfield: string) => Object.keys(errors).includes(pfield)) > -1 && <Icon name='exclamation circle' color='red' size='large'></Icon>}
                                        </>
                                    },
                                }))} />

                            <Divider />

                            {!values.isEnrollmentCompleted && <>
                                <BPButton type="button" primary floated="right" disabled={!isValid} onClick={async () => {
                                    if (isLastStep) {
                                        const result = await setFieldValue("isEnrollmentCompleted", true);
                                        if (!result)
                                            handleSubmit();
                                    } else {
                                        handleSubmit();
                                        const nextPane = panes.findIndex(p => p.to === location.pathname) + 1;
                                        navigate(panes[nextPane].to as any);
                                    }
                                }}>{isLastStep ? 'Save & Add New Enrollment' : 'Save & Next'}</BPButton>
                                <BPButton type="submit" floated="right" disabled={!isValid} >
                                    Save Progress
                                </BPButton>
                            </>}


                            {/* {<Grid>
                            <GridRow columns={2}>
                                <GridColumn>
                                    <div>{errors && <textarea value={JSON.stringify(errors, null, 2)} readOnly />}</div>
                                </GridColumn>
                                <GridColumn>
                                    <div>{values && <textarea value={JSON.stringify(values, null, 2)} readOnly />}</div>
                                </GridColumn>
                            </GridRow>
                        </Grid>} */}
                        </SForm>
                    )}
                </FormikWithSchema>
            </EnrollmentFormProvider>
        </BPContainer>
    );
};

export default EnrollmentForm;